import { useEffect, useState, useMemo } from "react";
import BarChart from "./BarChart";
import { CHART_BG_COLORS, CHART_BORDER_COLORS } from "../../utils/reports";
import {
  IdentifiedObjectItem,
  IdentifiedSpeciesCount,
} from "../../models/Media";
import { CameraItem } from "../../models/Camera";

interface SpeciesByYearProps {
  cameras: CameraItem[];
  selectedCameras: CameraItem[];
  allIdentifiedObjects: IdentifiedObjectItem[];
  selectedIdentifiedObjects: IdentifiedObjectItem[];
  reportData: IdentifiedSpeciesCount[];
  yearNames: string[];
}

export const preprocessReportData = (reportData: IdentifiedSpeciesCount[]) => {
  const groupedData = new Map();

  for (const data of reportData) {
    const year = new Date(data.date).getFullYear().toString();
    const key = `${data.identified_object_name}_${year}_${data.camera}`;

    if (!groupedData.has(key)) {
      groupedData.set(key, 0);
    }

    groupedData.set(key, groupedData.get(key) + 1);
  }

  return groupedData;
};

export const getTotalCount = (
  objectName: string,
  yearName: string,
  selectedCameras: CameraItem[],
  groupedReportData: Map<string, number>
): number | null => {
  let totalCount = 0;
  for (const camera of selectedCameras) {
    const key = `${objectName}_${yearName}_${camera.camera_id}`;
    totalCount += groupedReportData.get(key) || 0;
  }
  return totalCount > 0 ? totalCount : null;
};

export const getCount = (
  objectName: string,
  yearName: string,
  cameraId: string,
  groupedReportData: Map<string, number>
): number => {
  return groupedReportData.get(`${objectName}_${yearName}_${cameraId}`) || 0;
};

const LocationSpeciesByYear = ({
  selectedCameras,
  selectedIdentifiedObjects,
  reportData,
  yearNames,
}: SpeciesByYearProps) => {
  const [chartData, setChartData] = useState<any>(null);
  const [csvData, setCsvData] = useState<any>(null);
  const ChartName = "Location Species by Year";

  const groupedReportData = useMemo(
    () => preprocessReportData(reportData),
    [reportData]
  );

  useEffect(() => {
    if (selectedCameras && selectedIdentifiedObjects) {
      const csv = yearNames.flatMap((yearName) =>
        selectedCameras.flatMap((camera) =>
          selectedIdentifiedObjects.map((object) => ({
            camera: camera.camera_id,
            Object: object.name,
            Year: yearName,
            Count: getCount(
              object.name,
              yearName,
              camera.camera_id,
              groupedReportData
            ),
          }))
        )
      );
      setCsvData(csv);

      const data = {
        labels: yearNames.map((year) => year.toString()),
        datasets: selectedIdentifiedObjects.map((object, index) => ({
          label: object.name,
          data: yearNames.map((year) =>
            getTotalCount(object.name, year, selectedCameras, groupedReportData)
          ),
          backgroundColor: CHART_BG_COLORS[Object.keys(CHART_BG_COLORS)[index]],
          borderColor:
            CHART_BORDER_COLORS[Object.keys(CHART_BORDER_COLORS)[index]],
          borderWidth: 1,
        })),
      };

      setChartData(data);
    }
  }, [
    selectedCameras,
    selectedIdentifiedObjects,
    yearNames,
    groupedReportData,
  ]);

  const getYAxisConfig = (datasets: any[]) => {
    if (!datasets || datasets.length === 0)
      return { suggestedMax: 10, stepSize: 1 };

    const allValues = datasets.flatMap((dataset) =>
      dataset.data.map((d: any) => d ?? 0)
    );
    const maxValue = Math.max(...allValues);

    let stepSize = 10;
    let suggestedMax = Math.ceil(maxValue / 10) * 10;

    if (maxValue < 10) {
      stepSize = 1;
      suggestedMax = 10;
    } else if (maxValue < 70) {
      stepSize = 10;
    } else if (maxValue < 350) {
      stepSize = 50;
    } else if (maxValue < 1000) {
      stepSize = 100;
    } else {
      stepSize = 200;
    }

    return { suggestedMax, stepSize };
  };

  const { suggestedMax, stepSize } = chartData
    ? getYAxisConfig(chartData.datasets)
    : { suggestedMax: 10, stepSize: 1 };

  const options = {
    responsive: true,
    plugins: {
      legend: { position: "top" as const },
      title: {
        display: true,
        text: "Species Observed by Year",
        position: "top",
      },
    },
    layout: {
      padding: { left: 0, right: 10, top: 0, bottom: 0 },
    },
    scales: {
      y: { min: 0, suggestedMax, ticks: { stepSize } },
    },
  };

  return (
    <BarChart
      mapData={chartData}
      options={options}
      csvData={csvData}
      name={ChartName}
    />
  );
};

export default LocationSpeciesByYear;
